import type { FC, ReactNode } from 'react';
import type React from 'react';
import { Component, createContext, useContext } from 'react';
import { useTranslation } from 'react-i18next';
import { generatePath } from 'react-router-dom';
import type { User } from '@sentry/core';
import type { AxiosResponse } from 'axios';
import type { i18n, TFunction } from 'i18next';
import shortId from 'shortid';

import { DatalineStatus, LogicalOperator } from 'src/constants/dataline';
import { NO_REPORT_CONFIGURATION_VALUE } from 'src/constants/report';
import { SECTION } from 'src/constants/section';
import { API_RESULT_LIMITS } from 'src/constants/settings';
import type { DashboardShaping } from 'src/types/dashboard';
import type {
  ComparisonFrontend,
  DefinitionLineFrontend,
  OriginalDataline,
  ReportDatalineWrapper,
} from 'src/types/dataline';
import type { ModulePermission } from 'src/types/modulePermission';
import type {
  DefinitionLine,
  DragItem,
  ReportConfiguration,
  WorkspaceContextValue,
  WorkspaceState,
} from 'src/types/workspace';
import { apiPaths } from 'src/services/api/apiPaths';
import HttpService from 'src/services/api/http';
import { useUserContext } from 'src/components/app/context/UserContext';
import {
  CUSTOM_REPORT_DEFAULTS,
  WORKSPACE_DEFAULTS,
} from 'src/components/workspace/helper/constants';
import {
  addObjectAfterPosition,
  convertBackendToFrontendDatalines,
  convertFrontendToBackendDatalines,
  deleteObjectAtPosition,
  findDataPosition,
  generateDatalineDefinition,
  getDatalineErrors,
  mapDragItemToComparisonProperties,
  validateDataline,
} from 'src/components/workspace/helper/helper';
import {
  getCustomReportsHelper,
  getDatalineReturnsHelper,
  getDatalineTemplatesHelper,
} from 'src/components/workspace/WorkspaceContext/helpers/apiOperations';
import {
  changeConnectingOperatorHelper,
  createDatalineHelper,
  updateDatalineNameHelper,
  updateDatalineOrderHelper,
} from 'src/components/workspace/WorkspaceContext/helpers/datalineOperations';

export interface WorkspaceProviderProps {
  children: ReactNode;
  user: User | false;
  renewAutoLogout: () => void;
  i18n: i18n;
  t: TFunction;
}

export const WorkspaceContext = createContext<
  WorkspaceContextValue | undefined
>(undefined);

export const useWorkspaceContext = () => {
  const context = useContext(WorkspaceContext);
  if (context === undefined) {
    throw new Error(
      'useWorkspaceContext must be used within a WorkspaceProvider',
    );
  }
  return context;
};

/* Although the majority of context providers in the project are function-based, for now we're sticking
with the class component as efforts for maintenance work are currently more meaningfully focused elsewhere.
 */

export class WorkspaceProvider extends Component<
  WorkspaceProviderProps,
  WorkspaceState
> {
  state: WorkspaceState = JSON.parse(JSON.stringify(WORKSPACE_DEFAULTS));

  componentDidUpdate(prevProps: Readonly<WorkspaceProviderProps>) {
    const workspacePermission = this.getWorkspaceModulePermission();

    // Get the variable list with shapings as soon as the user logs in or the user changes.
    if (prevProps.user !== this.props.user) {
      if (workspacePermission) {
        this.resetWorkspace();
      } else {
        this.resetWorkspaceToDefaults();
      }
    }

    // Renew the auto logout while the workspace is being used.
    this.props.renewAutoLogout();
  }

  getWorkspaceModulePermission() {
    if (!this.props.user) return false;

    return this.props.user.permissions?.find(
      (permission: ModulePermission) =>
        permission?.module.id === SECTION.GET_REPORT_PRO,
    );
  }

  getCustomReports = async ({
    reset,
    sharedOnly,
    searchUrl,
    filters,
  }: {
    reset?: boolean;
    sharedOnly?: boolean;
    searchUrl?: string;
    filters?: Record<string, any>;
  }): Promise<void> => {
    const { customReports, customReportsPaginationStatus } = this.state;

    const result = await getCustomReportsHelper(
      customReports,
      customReportsPaginationStatus,
      { reset, sharedOnly, searchUrl, filters },
    );

    if (result.error) {
      this.showErrorMessage([result.error]);
    }

    this.setState({
      customReports: result.customReports,
      customReportsPaginationStatus: result.paginationStatus,
    });
  };

  getDatalineTemplates = async (reset = false): Promise<void> => {
    const { datalineTemplates, datalineTemplatesPaginationStatus } = this.state;

    const result = await getDatalineTemplatesHelper(
      datalineTemplates,
      datalineTemplatesPaginationStatus,
      reset,
    );

    if (result.error) {
      this.showErrorMessage([result.error]);
    }

    this.setState({
      datalineTemplates: result.datalineTemplates,
      datalineTemplatesPaginationStatus: result.paginationStatus,
    });
  };

  getDatalineReturns = async (
    datalinesToCheck: OriginalDataline[],
  ): Promise<void> => {
    const { updatedDatalines, error } = await getDatalineReturnsHelper(
      datalinesToCheck,
      this.state.currentReport.datalines,
    );

    if (error) {
      this.showErrorMessage([error]);
    }

    this.setState((prevState) => ({
      currentReport: {
        ...prevState.currentReport,
        datalines: updatedDatalines,
      },
    }));
  };

  hasDefaultReportConfiguration = (): boolean => {
    const { currentReport } = this.state;
    return [
      Number(currentReport.reportType),
      Number(currentReport.fileType),
      Number(currentReport.language),
    ].includes(NO_REPORT_CONFIGURATION_VALUE);
  };

  setReportConfiguration = (
    newReportConfiguration: ReportConfiguration,
    onDone: () => void,
  ): void => {
    this.setState(
      (prevState: Readonly<WorkspaceState>) => ({
        currentReport: {
          ...prevState.currentReport,
          fileType: Number(newReportConfiguration.fileType),
          language: Number(newReportConfiguration.language),
          reportName: newReportConfiguration.reportName,
          reportType: Number(newReportConfiguration.reportType),
        },
      }),
      onDone,
    );
  };

  resetWorkspace = (): void => {
    this.setState({
      currentReport: {
        ...CUSTOM_REPORT_DEFAULTS,
        datalines: [],
      },
    });
  };

  resetWorkspaceToDefaults = (): void => {
    this.setState(JSON.parse(JSON.stringify(WORKSPACE_DEFAULTS)));
  };

  resetReportConfiguration = (): void => {
    this.setState((prevState) => ({
      currentReport: {
        ...CUSTOM_REPORT_DEFAULTS,
        datalines: prevState.currentReport.datalines,
      },
    }));
  };

  addDataline = (dragItem: DragItem): void => {
    const {
      currentReport: { datalines },
    } = this.state;
    const selectedDataline = dragItem.item;
    if (!selectedDataline) return;

    const convertedDataline = convertBackendToFrontendDatalines([
      selectedDataline,
    ])[0];
    convertedDataline.driverAnalysis = datalines.length === 0;

    this.setState(
      (prevState) => ({
        currentReport: {
          ...prevState.currentReport,
          datalines: [...datalines, convertedDataline],
        },
      }),
      () => {
        if (
          !Object.values(DatalineStatus).includes(
            selectedDataline.dataline.status,
          )
        ) {
          void this.getDatalineReturns([convertedDataline]);
        }
      },
    );
  };

  addDatalinesFromReport = async (dragItem: DragItem): Promise<void> => {
    const {
      currentReport: { datalines },
    } = this.state;
    this.setShowLoadingIndicator(true);
    const endpoint = generatePath(apiPaths.REPORTS.DETAIL, { id: dragItem.id });

    try {
      const response = await HttpService.get(endpoint, undefined, false);
      const report = response.data;
      const reportDatalines = convertBackendToFrontendDatalines(
        report.datalines,
      );
      const driverAnalysisAlreadySet = datalines.find(
        (dataline) => dataline.driverAnalysis,
      );

      this.setState(
        (prevState) => ({
          currentReport: {
            ...prevState.currentReport,
            datalines: [
              ...datalines,
              ...reportDatalines.map((dataline_1) => ({
                ...dataline_1,
                driverAnalysis: driverAnalysisAlreadySet
                  ? false
                  : dataline_1.driverAnalysis,
              })),
            ],
          },
        }),
        () => {
          const datalinesToCheck = reportDatalines.filter(
            (reportDataline) =>
              reportDataline.status === undefined ||
              ![
                DatalineStatus.STATUS_OK,
                DatalineStatus.STATUS_TOO_FEW_NUMBER_OF_CASES,
                DatalineStatus.STATUS_AVOID_NEGATIVE_SELECTION,
              ].includes(reportDataline.status),
          );

          if (datalinesToCheck.length > 0) {
            this.getDatalineReturns(datalinesToCheck);
          }
        },
      );
      this.setShowLoadingIndicator(false, null);
    } catch (error: any) {
      this.showErrorMessage([error.response.data]);
      this.setShowLoadingIndicator(false, null);
    }
  };

  /**
   * Creates a new dataline from a drag item
   */
  createDataline = (dragItem: DragItem): void => {
    const {
      currentReport: { datalines },
    } = this.state;
    const datalineWithName = createDatalineHelper(dragItem, datalines.length);

    this.setState(
      (prevState: Readonly<WorkspaceState>) => ({
        currentReport: {
          ...prevState.currentReport,
          datalines: [...datalines, datalineWithName],
        },
      }),
      () => {
        void this.getDatalineReturns([datalineWithName]);
      },
    );
  };

  updateDataline = (dragItem: DragItem, renderId: string): void => {
    const { datalines } = this.state.currentReport;
    const comparisonProps = mapDragItemToComparisonProperties(
      dragItem,
      shortId.generate(),
    );
    const comparison = { ...comparisonProps, op: LogicalOperator.AND };
    const { datalineIndex } = findDataPosition(datalines, renderId);
    if (datalineIndex === null) return;

    const updatedDatalines = addObjectAfterPosition(
      datalines,
      renderId,
      comparison,
    );
    const currentDataline = updatedDatalines[datalineIndex];
    currentDataline.isSaved = false;
    currentDataline.status = DatalineStatus.STATUS_LOADING;
    currentDataline.returns = null;
    currentDataline.invalidities = validateDataline(currentDataline);

    updatedDatalines[datalineIndex] =
      generateDatalineDefinition(currentDataline);

    this.setState(
      (prevState: Readonly<WorkspaceState>) => ({
        currentReport: {
          ...prevState.currentReport,
          datalines: updatedDatalines,
        },
      }),
      () => {
        if (!currentDataline.invalidities)
          void this.getDatalineReturns([currentDataline]);
      },
    );
  };

  /**
   * Updates the name of a dataline by its renderId
   */
  updateDatalineName = (
    datalineName: string,
    datalineRenderId: string,
  ): void => {
    const { datalines } = this.state.currentReport;
    const updatedDatalines = updateDatalineNameHelper(
      datalines,
      datalineName,
      datalineRenderId,
    );

    this.setState((prevState) => ({
      currentReport: {
        ...prevState.currentReport,
        datalines: updatedDatalines,
      },
    }));
  };

  /**
   * Reorders datalines by moving a dataline from dragIndex to hoverIndex
   */
  updateDatalineOrder = (dragIndex: number, hoverIndex: number) => {
    const reorderedDatalines = updateDatalineOrderHelper(
      this.state.currentReport.datalines,
      dragIndex,
      hoverIndex,
    );

    this.setState((prevState: Readonly<WorkspaceState>) => ({
      currentReport: {
        ...prevState.currentReport,
        datalines: reorderedDatalines,
      },
    }));
  };

  saveDatalineAsTemplate = async (datalineRenderId: string): Promise<void> => {
    const { datalines } = this.state.currentReport;
    const endpoint = apiPaths.TEMPLATE;
    const foundDataline = datalines.find(
      (dataline) => dataline.renderId === datalineRenderId,
    );

    if (!foundDataline) return;

    const selectedDataline = { ...foundDataline };
    const validationErrors = getDatalineErrors(selectedDataline);

    const insertDatalineIntoState = () =>
      this.setState((prevState) => ({
        currentReport: {
          ...prevState.currentReport,
          datalines: prevState.currentReport.datalines.map((dl) =>
            dl.renderId === datalineRenderId ? selectedDataline : dl,
          ),
        },
      }));

    if (validationErrors.length > 0) {
      selectedDataline.invalidities = true;
      insertDatalineIntoState();
      this.showErrorMessage(validationErrors);
      return;
    }

    const backendDataline = convertFrontendToBackendDatalines([
      selectedDataline,
    ])[0];
    const payload = JSON.stringify(backendDataline);

    try {
      await HttpService.post(endpoint, payload);

      selectedDataline.isSaved = true;
      await this.getDatalineTemplates(true);
      insertDatalineIntoState();
    } catch (error: any) {
      this.showErrorMessage([error.response?.data]);
    }
  };

  clearDatalines = () => {
    this.setState((prevState) => ({
      currentReport: { ...prevState.currentReport, datalines: [] },
    }));
  };

  /**
   * Changes the connecting operator (AND/OR) between comparisons or definition lines
   */
  changeConnectingOperator = (
    comparisonRenderId: string,
    operator: LogicalOperator,
    definitionlineRenderId?: string | null,
  ) => {
    const { datalines } = this.state.currentReport;
    const { updatedDatalines, datalineToRefresh } =
      changeConnectingOperatorHelper(
        datalines,
        comparisonRenderId,
        operator,
        definitionlineRenderId,
      );

    if (updatedDatalines && datalineToRefresh) {
      this.setState(
        (prevState) => ({
          currentReport: {
            ...prevState.currentReport,
            datalines: updatedDatalines,
          },
        }),
        () => {
          if (!datalineToRefresh.invalidities) {
            this.getDatalineReturns([datalineToRefresh]);
          }
        },
      );
    }
  };

  replaceOrgUnitDummy = (dragItem: DragItem, dummyParentRenderId: string) => {
    const updatedDatalines = [...this.state.currentReport.datalines];
    const { datalineIndex, definitionlineIndex } = findDataPosition(
      updatedDatalines,
      dummyParentRenderId,
    );

    if (datalineIndex === null) {
      return;
    }

    const definitionlines = updatedDatalines[datalineIndex].definitionlines;

    if (!definitionlines) {
      return;
    }

    const mappedDragItem = mapDragItemToComparisonProperties(
      dragItem,
      shortId.generate(),
    );
    mappedDragItem.op = LogicalOperator.AND;

    const currentDataline = {
      ...updatedDatalines[datalineIndex],
      definitionlines: definitionlines.map((definitionline, j) =>
        j !== definitionlineIndex
          ? definitionline
          : {
              ...definitionline,
              comparisons: [mappedDragItem, ...definitionline.comparisons],
            },
      ),
    };

    currentDataline.isSaved = false;
    currentDataline.invalidities = validateDataline(currentDataline);
    currentDataline.status = DatalineStatus.STATUS_LOADING;
    currentDataline.returns = null;

    updatedDatalines[datalineIndex] =
      generateDatalineDefinition(currentDataline);

    this.setState(
      (prevState) => ({
        currentReport: {
          ...prevState.currentReport,
          datalines: updatedDatalines,
        },
      }),
      () => {
        if (!currentDataline.invalidities) {
          this.getDatalineReturns([currentDataline]);
        }
      },
    );
  };

  addValueToExistingShaping = (dragItem: DragItem, shapingRenderId: string) => {
    const { datalines } = this.state.currentReport;
    const { datalineIndex, definitionlineIndex, comparisonIndex } =
      findDataPosition(datalines, shapingRenderId);
    if (datalineIndex === null) return;

    const mappedDragItem = mapDragItemToComparisonProperties(
      dragItem,
      shortId.generate(),
    );
    const currentDatalineCopy = JSON.parse(
      JSON.stringify({
        ...datalines[datalineIndex],
        renderId: shortId.generate(),
        isSaved: false,
      }),
    );

    if (definitionlineIndex !== null && comparisonIndex !== null) {
      currentDatalineCopy.definitionlines[definitionlineIndex].comparisons[
        comparisonIndex
      ].valueNames.push(mappedDragItem.valueNames[0]);

      currentDatalineCopy.definitionlines[definitionlineIndex].comparisons[
        comparisonIndex
      ].values.push(mappedDragItem.values[0]);
    }

    currentDatalineCopy.status = DatalineStatus.STATUS_LOADING;
    currentDatalineCopy.returns = null;

    this.setState(
      (prevState) => ({
        currentReport: {
          ...prevState.currentReport,
          datalines: datalines.map((dataline, index) =>
            index !== datalineIndex
              ? dataline
              : generateDatalineDefinition(currentDatalineCopy),
          ),
        },
      }),
      () => {
        this.getDatalineReturns([currentDatalineCopy]);
      },
    );
  };

  addShapingToAllDefinitionLines = (dragItem: DragItem): void => {
    const comparisonProps = mapDragItemToComparisonProperties(dragItem, '');

    const datalines = this.state.currentReport.datalines.map((dataline) => {
      if (dataline.isReadOnly) return dataline;

      const currentDatalineCopy = JSON.parse(
        JSON.stringify({
          ...dataline,
          renderId: shortId.generate(),
          isSaved: false,
        }),
      ); // deep clone needed

      currentDatalineCopy.definitionlines.forEach(
        (definitionline: DefinitionLineFrontend) => {
          const comparison = {
            ...comparisonProps,
            op: LogicalOperator.AND,
            renderId: shortId.generate(),
          };

          const comparisonIndex = definitionline.comparisons.findIndex(
            (comparison: ComparisonFrontend) =>
              comparison.variableName === comparisonProps.variableName,
          );

          // If we have an index it means there is already an attribute of the same type we want
          // to add. So we need to combine them instead of just adding it as another 'AND' parameter.
          if (comparisonIndex >= 0) {
            comparisonProps.values.forEach((value) => {
              const existingValues = definitionline.comparisons[comparisonIndex]
                .values as (string | number)[];
              if (!existingValues.includes(value as string | number)) {
                existingValues.push(value as string | number);
              }
            });

            comparisonProps.valueNames.forEach((name) => {
              if (
                !definitionline.comparisons[
                  comparisonIndex
                ].valueNames.includes(name)
              ) {
                return definitionline.comparisons[
                  comparisonIndex
                ].valueNames.push(name);
              }
            });
          } else {
            definitionline.comparisons.push(comparison);
          }
        },
      );

      if (
        currentDatalineCopy.invalidities &&
        !validateDataline(currentDatalineCopy)
      ) {
        currentDatalineCopy.invalidities = false;
      }

      currentDatalineCopy.status = DatalineStatus.STATUS_LOADING;
      currentDatalineCopy.returns = null;

      return generateDatalineDefinition(currentDatalineCopy);
    });

    this.setState(
      (prevState) => ({
        currentReport: { ...prevState.currentReport, datalines },
      }),
      () => {
        this.setShowLoadingIndicator(false, null);

        this.getDatalineReturns(datalines);
      },
    );
  };

  setShowLoadingIndicator = (
    status: boolean,
    callback?: (() => void) | null,
  ): void => {
    this.setState({ loadingIndicatorIsShown: status }, () => {
      // allows React to render the spinner before executing the callback function
      !!callback && setTimeout(callback);
    });
  };

  /**
   * @description takes an existing dataline and creates a copy for each shaping of the provided variable;
   *  adds the corresponding shaping from the dataline copy to each definition line.
   * @param dragItem
   * @param datalineRenderId
   */
  breakVariableIntoNewDatalines = async (
    dragItem: DragItem,
    datalineRenderId: string,
  ) => {
    const { t } = this.props;
    const { currentReport } = this.state;
    const datalines = [...currentReport.datalines];

    const { variableId, variableShortName } = dragItem;
    let shapings: DashboardShaping[] | undefined = dragItem.shapings;
    const { datalineIndex } = findDataPosition(datalines, datalineRenderId);
    if (datalineIndex === null || variableId === undefined) return;

    if (!shapings) {
      const moduleId = SECTION.GET_REPORT_PRO;
      const requestEndpoint = apiPaths.SHAPING_BY_VARIABLE_ID + variableId;

      shapings = await HttpService.get(requestEndpoint, {
        limit: API_RESULT_LIMITS.SEARCH,
        module_id: moduleId,
      })
        .then(
          (response: AxiosResponse<{ results: DashboardShaping[] }>) =>
            response.data.results,
        )
        .catch((error) => {
          this.showErrorMessage([error.response?.data]);
          return Promise.resolve(undefined);
        });
    }

    if (!shapings) {
      this.showErrorMessage('missing shapings');
      return;
    }

    // limit shapings (and therefore created datalines) to 500 items
    if (shapings.length > 500) {
      shapings.length = 500;
    }

    // loop through the shapings and create a new dataline for each one
    shapings.forEach((shaping, shapingIndex) => {
      if (shapingIndex === 499) {
        this.showErrorMessage([{ 0: t('dataLineLimitError') }]);
      }

      // deep-clone the dataline
      let currentDatalineCopy = JSON.parse(
        JSON.stringify({
          ...datalines[datalineIndex],
          renderId: shortId.generate(),
          isSaved: false,
        }),
      );

      // the cloned dataline's definition lines and comparisons all need a new renderId
      const updatedDefinitionlines = currentDatalineCopy.definitionlines.map(
        (definitionline: DefinitionLine) => {
          const comparisonToAdd = {
            renderId: shortId.generate(),
            op: LogicalOperator.AND,
            valueNames: [shaping.name],
            values: [shaping.shaping_id],
            variable: variableId,
            variableName: variableShortName || '',
          };

          const updatedComparisons = definitionline.comparisons.map(
            (comparison) => ({
              ...comparison,
              renderId: shortId.generate(),
            }),
          );

          updatedComparisons.push(comparisonToAdd);

          return {
            ...definitionline,
            renderId: shortId.generate(),
            comparisons: updatedComparisons,
          };
        },
      );

      currentDatalineCopy = {
        ...currentDatalineCopy,
        definitionlines: updatedDefinitionlines,
      };

      currentDatalineCopy.status = DatalineStatus.STATUS_LOADING;
      currentDatalineCopy.returns = null;
      currentDatalineCopy.driverAnalysis = false;

      const namedDataline = generateDatalineDefinition(currentDatalineCopy);

      datalines.splice(datalineIndex + shapingIndex + 1, 0, namedDataline);
    });

    this.setState(
      (prevState) => ({
        currentReport: { ...prevState.currentReport, datalines },
      }),
      () => {
        this.setShowLoadingIndicator(false, null);

        this.getDatalineReturns(datalines);
      },
    );
  };

  deleteByRenderId = (renderId: string): void => {
    const datalines = [...this.state.currentReport.datalines];
    const positions = findDataPosition(datalines, renderId);
    const { datalineIndex, definitionlineIndex } = positions;

    if (datalineIndex === null) {
      return;
    }

    const newDatalines = deleteObjectAtPosition(datalines, renderId);
    const currentDataline = newDatalines[datalineIndex];

    if (currentDataline) {
      currentDataline.invalidities = validateDataline(currentDataline);

      // name should only be generated if something changed inside a dataline, not if another dataline was removed
      if (definitionlineIndex !== null) {
        currentDataline.isSaved = false;
        if (currentDataline.definitionlines) {
          currentDataline.status = currentDataline.definitionlines[0]
            .comparisons.length
            ? DatalineStatus.STATUS_LOADING
            : DatalineStatus.STATUS_ERROR;
        }
        currentDataline.returns = null;
        newDatalines[datalineIndex] =
          generateDatalineDefinition(currentDataline);
      }
    }

    this.setState(
      (prevState) => ({
        currentReport: { ...prevState.currentReport, datalines: newDatalines },
      }),
      () => {
        if (
          definitionlineIndex !== null &&
          currentDataline.definitionlines[0].comparisons.length &&
          !currentDataline.invalidities
        ) {
          this.getDatalineReturns([currentDataline]);
        }
      },
    );
  };

  deleteReport = async (reportId: number): Promise<void> => {
    const endpoint = generatePath(apiPaths.REPORTS.DETAIL, { id: reportId });

    try {
      await HttpService.delete(endpoint, {});
      return await this.getCustomReports({ reset: true });
    } catch (error: any) {
      this.showErrorMessage([error.response.data]);
    }
  };

  deleteDatalineTemplate = async (templateId: number): Promise<void> => {
    const endpoint = `${apiPaths.TEMPLATE}${templateId}/`;

    try {
      await HttpService.delete(endpoint, {});
      return await this.getDatalineTemplates(true);
    } catch (error: any) {
      this.showErrorMessage([error.response.data]);
    }
  };

  /**
   * SaveDraft saves a report.
   * When a report is ordered, the afterSave method is called and the report is flagged as 'ordered'.
   */
  saveDraft = async (afterSave?: (reportId: number) => void): Promise<void> => {
    const { currentReport } = this.state;
    const endpoint = apiPaths.REPORTS.LIST;
    const backendDatalines = convertFrontendToBackendDatalines(
      currentReport.datalines,
    );

    const payload = JSON.stringify({
      datalines: backendDatalines,
      file_type: currentReport.fileType,
      is_custom_report: true,
      language: currentReport.language,
      name: currentReport.reportName,
      report_type: currentReport.reportType,
    });

    try {
      const response = await HttpService.post(endpoint, payload);
      if (afterSave) {
        this.setState(
          (prevState) => ({
            currentReport: {
              ...prevState.currentReport,
              id: response.data.id,
            },
          }),
          () => afterSave(response.data.id),
        );
      } else {
        this.resetWorkspace();
        await this.getCustomReports({ reset: true });
      }
    } catch (error: any) {
      this.resetReportConfiguration();
      this.showErrorMessage([error.response.data]);
    }
  };

  /**
   * Before a report can be marked as ordered, it must be created with the 'saveDraft' function.
   * The id of the function is then needed for the POST request 'order_report'.
   * Therefore, the 'orderRequest' function is passed to the 'saveDraft' function.
   * The order is important so that 'saveDraft' is called first and then the 'order_report' request.
   */
  orderReport = (keepReportInWorkspace?: boolean): Promise<void> => {
    const orderRequest = (reportId: number): Promise<void> =>
      HttpService.patch(
        generatePath(apiPaths.ORDER_REPORT, { id: reportId }),
        {},
      )
        .then(() => {
          if (!keepReportInWorkspace) this.resetWorkspace();
          this.getCustomReports({ reset: true });
        })
        .catch((error) => {
          this.resetReportConfiguration();
          this.showErrorMessage([error.response.data]);
        });

    return this.saveDraft(orderRequest);
  };

  showErrorMessage = (errors: any = []): void => {
    const { t } = this.props;
    let errorMessage = '';
    const uniqueErrors = Array.from(new Set(errors));

    uniqueErrors.forEach((error) => {
      if (!error) return;
      let renderedError = 'REQUEST_FAILED';
      if (typeof error === 'object') {
        if (Array.isArray(error)) {
          [renderedError] = error;
        } else if ('response' in error) {
          renderedError = error.response as string;
        }
      } else if (typeof error === 'string') {
        renderedError = error;
      }
      errorMessage += `${t(`errorMessages.${renderedError}`, renderedError)}`;
    });

    this.setState({ errorMessage });
  };

  validateReportConfiguration = () => {
    const {
      currentReport: { datalines: datalinesToCheck },
    } = this.state;
    let errors: any[] = [];

    if (datalinesToCheck.length === 0) {
      errors.push('EMPTY_REPORT');
    }

    datalinesToCheck.map((dataline) => {
      const datalineErrors = getDatalineErrors(dataline);
      const datalineToCheck = dataline;

      if (datalineErrors.length > 0) {
        errors = errors.concat(datalineErrors);
        datalineToCheck.invalidities = true;
      }

      return datalineToCheck;
    });

    this.setState((prevState) => ({
      currentReport: { ...prevState.currentReport, datalinesToCheck },
    }));

    if (errors.length === 0) {
      return true;
    }

    this.showErrorMessage(errors);
    return false;
  };

  getDatalineDefinitionFromBackendObject = (
    dataline: ReportDatalineWrapper,
  ) => {
    const frontendObject = convertBackendToFrontendDatalines([dataline]);
    return generateDatalineDefinition(frontendObject[0]).name;
  };

  setDriverAnalysis = (datalineRenderId: string, checked: boolean) => {
    const {
      currentReport: { datalines },
    } = this.state;

    const datalinesToCheck = datalines.map((dataline) => ({
      ...dataline,
      driverAnalysis: dataline.renderId === datalineRenderId ? checked : false,
    }));

    this.setState((prevState) => ({
      currentReport: {
        ...prevState.currentReport,
        datalines: datalinesToCheck,
      },
    }));
  };

  getDriverAnalysisSet = () =>
    !!this.state.currentReport.datalines.find(
      (dataline) => dataline.driverAnalysis,
    );

  render() {
    const {
      currentReport,
      customReports,
      customReportsPaginationStatus,
      datalineTemplates,
      datalineTemplatesPaginationStatus,
      errorMessage,
      loadingIndicatorIsShown,
    } = this.state;
    const { children } = this.props;

    return (
      <WorkspaceContext.Provider
        value={{
          addDataline: this.addDataline,
          addDatalinesFromReport: this.addDatalinesFromReport,
          addShapingToAllDefinitionLines: this.addShapingToAllDefinitionLines,
          addValueToExistingShaping: this.addValueToExistingShaping,
          breakVariableIntoNewDatalines: this.breakVariableIntoNewDatalines,
          changeConnectingOperator: this.changeConnectingOperator,
          clearDatalines: this.clearDatalines,
          createDataline: this.createDataline,
          currentReport,
          customReports,
          customReportsPaginationStatus,
          datalineTemplates,
          datalineTemplatesPaginationStatus,
          deleteByRenderId: this.deleteByRenderId,
          deleteDatalineTemplate: this.deleteDatalineTemplate,
          deleteReport: this.deleteReport,
          errorMessage,
          getCustomReports: this.getCustomReports,
          getDatalineDefinitionFromBackendObject:
            this.getDatalineDefinitionFromBackendObject,
          getDatalineTemplates: this.getDatalineTemplates,
          getDriverAnalysisSet: this.getDriverAnalysisSet,
          loadingIndicatorIsShown,
          orderReport: this.orderReport,
          replaceOrgUnitDummy: this.replaceOrgUnitDummy,
          resetReportConfiguration: this.resetReportConfiguration,
          resetWorkspace: this.resetWorkspace,
          resetWorkspaceToDefaults: this.resetWorkspaceToDefaults,
          saveDatalineAsTemplate: this.saveDatalineAsTemplate,
          saveDraft: this.saveDraft,
          setDriverAnalysis: this.setDriverAnalysis,
          hasDefaultReportConfiguration: this.hasDefaultReportConfiguration,
          setReportConfiguration: this.setReportConfiguration,
          setShowLoadingIndicator: this.setShowLoadingIndicator,
          showErrorMessage: this.showErrorMessage,
          updateDataline: this.updateDataline,
          updateDatalineName: this.updateDatalineName,
          updateDatalineOrder: this.updateDatalineOrder,
          validateReportConfiguration: this.validateReportConfiguration,
        }}
      >
        {children}
      </WorkspaceContext.Provider>
    );
  }
}

const WorkspaceProviderWrapper: FC<{ children: ReactNode }> = ({
  children,
}) => {
  const { i18n, t } = useTranslation('report');
  const { user, renewAutoLogout } = useUserContext();

  return (
    <WorkspaceProvider
      renewAutoLogout={renewAutoLogout}
      user={user}
      i18n={i18n}
      t={t}
    >
      {children}
    </WorkspaceProvider>
  );
};

export default WorkspaceProviderWrapper;
